import React, { useEffect, useMemo, useState } from 'react'
import { nanoid } from 'nanoid'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Code, ConnectError } from '@connectrpc/connect'

import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import Section1 from './sections/Section1'
import Section2 from './sections/Section2'
import Funding from './sections/Funding'
import Notary from './sections/Notary'
import Files from './sections/Files'
import Terms from './sections/Terms'
import DynamicFields from './sections/DynamicFields'
import ButtonIconText from '@components/buttons/ButtonIconText'
import {
	addPerson,
	removePerson,
	resetPersons,
	setAddress,
	setBirthCity,
	setBirthCountry,
	setBirthDate,
	setChildren,
	setContractDate,
	setContractPlace,
	setContractType,
	setEmail,
	setFirstName,
	setGender,
	setHasContract,
	setIdFile,
	setLastName,
	setMarriageCity,
	setMarriageDate,
	setMarried,
	setNationality,
	setNonFinancingDeclarationFile,
	setPhone,
	setProfession,
} from './sections/personSlice'
import {
	addCorporation,
	removeCorporation,
	resetCorporations,
	setCorporationAddress,
	setCorporationCapital,
	setCorporationDelegateAddress,
	setCorporationDelegateEmail,
	setCorporationDelegateFirstName,
	setCorporationDelegateGender,
	setCorporationDelegateIdFile,
	setCorporationDelegateLastName,
	setCorporationDelegatePhone,
	setCorporationKbisFile,
	setCorporationName,
	setCorporationNonFinancingDeclarationFile,
	setCorporationRegistar,
	setCorporationSiren,
	setCorporationStatutsFile,
} from './sections/corporationSlice'
import { resetFunding, setContributionAmount, setInterestRate, setLoanAmount, setLoanDuration } from './sections/fundingSlice'
import { resetNotary, setCity, setHasNotary, setName } from './sections/notarySlice'
import { setTermsAndConditions } from './sections/termsSlice'
import arrow from '@assets/form/arrow.svg'
import ajouter from '@assets/form/ajouter.svg'
import { useValidatorContext } from '@/utils'
import { useForm } from '@/providers/formProvider'
import Modal from './Modal'
import modalArrow from '@assets/form/modal-arrow.png'
import Button from '@components/buttons/Button'
import { generateFormDefinition, type DynamicFieldDefinition } from './formDefinition'
import { FieldRules } from './fieldRules'
import type { Section1Values } from './sections/Section1'
import Layout from '@components/layout/Layout'
import { useSessionTokenVerification } from '@/hooks/useSessionToken'


import './Form.css'

type JsonMap = Record<string, unknown>

const EMPTY_SECTION1_VALUES: Section1Values = {
	accommodationType: '',
	rooms: '',
	residenceType: '',
	managedResidenceType: '',
	lotNumber: '',
	area: '',
	parking: '',
	usage: '',
	vat: '',
	price: '',
}

const DEFAULT_PROGRAM_NAME = 'Les hauts de Cannes'
const DEFAULT_SUBTITLE = 'Cannes'
const MAPPED_DYNAMIC_KEYS = new Set<string>([
	// buyer type discriminator
	'buyerType',
	// person fields (new schema keys)
	'gender',
	'lastName',
	'firstName',
	'phone',
	'email',
	'birthDate',
	'birthCity',
	'birthCountry',
	'nationality',
	'profession',
	'address',
	'maritalStatus',
	'children',
	'marriageCity',
	'marriageDate',
	'hasContract',
	'contractDate',
	'contractPlace',
	'contractType',
	'idFile',
	'nonFinancingDeclarationFile',
	// corporation fields (new schema keys)
	'corporationName',
	'corporationCapital',
	'corporationAddress',
	'corporationSiren',
	'corporationRegistar',
	'delegateGender',
	'delegateLastName',
	'delegateFirstName',
	'delegatePhone',
	'delegateEmail',
	'delegateAddress',
	'delegateIdFile',
	'corporationKbisFile',
	'corporationStatutsFile',
	'corporationNonFinancingDeclarationFile',
	// funding
	'fundingType',
	'contributionAmount',
	'loanAmount',
	'loanDuration',
	'interestRate',
	// notary
	'hasNotary',
	'notaryName',
	'notaryCity',
	// terms
	'termsAndConditions',
])

const FORM_STATUS_COMPLETED = 3

function formatSubmittedAt(iso: string): string {
	try {
		const date = new Date(iso)
		const day = date.getDate().toString().padStart(2, '0')
		const month = (date.getMonth() + 1).toString().padStart(2, '0')
		const year = date.getFullYear()
		const hours = date.getHours().toString().padStart(2, '0')
		const minutes = date.getMinutes().toString().padStart(2, '0')
		return `${day}/${month}/${year} à ${hours}:${minutes}`
	} catch {
		return iso
	}
}


function toStringValue(value: unknown): string {
	if (value === undefined || value === null) return ''
	return String(value)
}

function isTruthy(value: unknown): boolean {
	const normalized = toStringValue(value).trim().toLowerCase()
	return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'oui'
}

function getObjectValue(value: unknown): JsonMap | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null
	return value as JsonMap
}

function getObjectArray(value: unknown): JsonMap[] {
	if (!Array.isArray(value)) return []
	return value
		.map(item => getObjectValue(item))
		.filter((item): item is JsonMap => item !== null)
}

function toHasNotaryRadioValue(value: unknown): string {
	const normalized = toStringValue(value).trim().toLowerCase()
	if (
		normalized === 'hasnotary-yes' ||
		normalized === 'yes' ||
		normalized === 'true' ||
		normalized === '1'
	) {
		return 'hasNotary-yes'
	}
	if (
		normalized === 'hasnotary-no' ||
		normalized === 'no' ||
		normalized === 'false' ||
		normalized === '0'
	) {
		return 'hasNotary-no'
	}
	return ''
}

function fromHasNotaryRadioValue(value: string): string {
	if (value === 'hasNotary-yes') return 'true'
	if (value === 'hasNotary-no') return 'false'
	return ''
}

function buildDynamicPayload(params: {
	loadedDynamicValues: Record<string, string>
	buyerType: string
	fundingType: string
	persons: RootState['person']['persons']
	corporations: RootState['corporation']['corporations']
	funding: RootState['funding']
	notary: RootState['notary']
	termsAndConditions: boolean
}): Record<string, string> {
	const payload: Record<string, string> = {
		...params.loadedDynamicValues,
		buyerType: params.buyerType,
		fundingType: params.fundingType,
		contributionAmount: params.funding.contributionAmount,
		loanAmount: params.funding.loanAmount,
		loanDuration: params.funding.loanDuration,
		interestRate: params.funding.interestRate,
		hasNotary: fromHasNotaryRadioValue(params.notary.hasNotary),
		notaryName: params.notary.name,
		notaryCity: params.notary.city,
		termsAndConditions: params.termsAndConditions ? 'true' : 'false',
	}

	const primaryPerson = params.persons[0]
	const primaryCorporation = params.corporations[0]

	if (params.buyerType === 'corporation') {
		payload.delegateGender = primaryCorporation?.delegateGender ?? ''
		payload.corporationName = primaryCorporation?.name ?? ''
		payload.corporationCapital = primaryCorporation?.capital ?? ''
		payload.corporationAddress = primaryCorporation?.address ?? ''
		payload.corporationSiren = primaryCorporation?.siren ?? ''
		payload.corporationRegistar = primaryCorporation?.registar ?? ''
		payload.primaryLastName = primaryCorporation?.delegateLastName ?? ''
		payload.primaryFirstName = primaryCorporation?.delegateFirstName ?? ''
		payload.primaryPhone = primaryCorporation?.delegatePhone ?? ''
		payload.primaryEmail = primaryCorporation?.delegateEmail ?? ''
		payload.address = primaryCorporation?.delegateAddress ?? primaryCorporation?.address ?? ''
		payload.idDocumentPath = primaryCorporation?.delegateIdFile ?? ''
		payload.proofFundsPath = primaryCorporation?.nonFinancingDeclarationFile ?? ''
		payload.kbisFile = primaryCorporation?.kbisFile ?? ''
		payload.statutsFile = primaryCorporation?.statutsFile ?? ''
		return payload
	}

	payload.gender = primaryPerson?.gender ?? ''
	payload.primaryLastName = primaryPerson?.lastName ?? ''
	payload.primaryFirstName = primaryPerson?.firstName ?? ''
	payload.primaryPhone = primaryPerson?.phone ?? ''
	payload.primaryEmail = primaryPerson?.email ?? ''
	payload.birthDate = primaryPerson?.birthDate ?? ''
	payload.birthCity = primaryPerson?.birthCity ?? ''
	payload.birthCountry = primaryPerson?.birthCountry ?? ''
	payload.nationality = primaryPerson?.nationality ?? ''
	payload.profession = primaryPerson?.profession ?? ''
	payload.address = primaryPerson?.address ?? ''
	payload.maritalStatus = primaryPerson?.married ?? ''
	payload.childrenCount = primaryPerson?.children ?? ''
	payload.marriageCity = primaryPerson?.marriageCity ?? ''
	payload.marriageDate = primaryPerson?.marriageDate ?? ''
	payload.hasContract = primaryPerson?.hasContract ?? ''
	payload.contractDate = primaryPerson?.contractDate ?? ''
	payload.contractPlace = primaryPerson?.contractPlace ?? ''
	payload.contractType = primaryPerson?.contractType ?? ''
	payload.idDocumentPath = primaryPerson?.idFile ?? ''
	payload.proofFundsPath = primaryPerson?.nonFinancingDeclarationFile ?? ''

	return payload
}

// submitError tells the buyer why their submission did not go through. A
// refusal carries the server's own reason, which names the missing answers;
// anything else is a technical failure the buyer can only retry.
function submitError(error: unknown): string {
	const connectError = ConnectError.from(error)
	if (connectError.code === Code.InvalidArgument && connectError.rawMessage) {
		return `Le formulaire n'a pas pu être envoyé, ${connectError.rawMessage}.`
	}
	return "Le formulaire n'a pas pu être envoyé. Veuillez réessayer dans quelques instants."
}

function Form() {
	const validator = useValidatorContext()
	const form = useForm()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { token: adminToken, isVerifying: isVerifyingToken } = useSessionTokenVerification({ mode: 'optional' })
	const isAdmin = adminToken !== ''
	const [searchParams] = useSearchParams()
	const shareToken = searchParams.get('token') ?? ''

	const [buyerType, setBuyerType] = useState('')
	const [fundingType, setFundingType] = useState('')
	const [openModal, setOpenModal] = useState(false)
	// Which admin action is waiting on the incomplete-form confirmation. Saving
	// and validating both offer to go ahead, but validating also mails the
	// buyers, so the two cannot share one flag.
	const [submitErrorMessage, setSubmitErrorMessage] = useState('')
	// Confirms a buyer's draft save, which keeps them on the page.
	const [savedMessage, setSavedMessage] = useState('')
	const [pendingIncomplete, setPendingIncomplete] = useState<'save' | 'validate' | null>(null)
	const [openValidatedModal, setOpenValidatedModal] = useState(false)
	// Submitting uploads the whole form, files included, so it is slow enough
	// that the button has to show it is working.
	const [submitting, setSubmitting] = useState(false)
	const [hasValidationError, setHasValidationError] = useState(false)
	const [readonly, setReadonly] = useState(false)
	const [programName, setProgramName] = useState(DEFAULT_PROGRAM_NAME)
	const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE)
	const [section1Values, setSection1Values] = useState<Section1Values>(EMPTY_SECTION1_VALUES)
	const [loadedDynamicValues, setLoadedDynamicValues] = useState<Record<string, string>>({})
	const [dynamicFields, setDynamicFields] = useState<DynamicFieldDefinition[]>([])
	// What the schema says about each field: whether it is asked, mandatory, and
	// when it appears. The sections take all of it from here rather than
	// deciding for themselves.
	const rules = useMemo(() => new FieldRules(dynamicFields), [dynamicFields])
	const [isLoadingDefinition, setIsLoadingDefinition] = useState(true)
	const [definitionError, setDefinitionError] = useState('')
	const [formStatus, setFormStatus] = useState(0)
	const [formSubmittedAt, setFormSubmittedAt] = useState('')
	const [searchQuery, setSearchQuery] = useState('')

	const persons = useAppSelector((state: RootState) => state.person.persons)
	const corporations = useAppSelector((state: RootState) => state.corporation.corporations)
	const funding = useAppSelector((state: RootState) => state.funding)
	const notary = useAppSelector((state: RootState) => state.notary)
	const termsAndConditions = useAppSelector((state: RootState) => state.terms)

	const handleAddBuyer = () => {
		const id = nanoid()
		dispatch(addPerson(id))
		dispatch(addCorporation(id))
	}

	const handleSetBuyerType = (value: string) => {
		setBuyerType(value)
	}

	const handleSetFundingType = (value: string) => {
		setFundingType(value)
	}

	const handleRemoveBuyer = (id: string) => {
		dispatch(removePerson(id))
		dispatch(removeCorporation(id))
	}

	useEffect(() => {
		let cancelled = false

		const resetLegacyState = () => {
			dispatch(resetPersons())
			dispatch(resetCorporations())
			dispatch(resetFunding())
			dispatch(resetNotary())
			dispatch(setTermsAndConditions(false))
			setBuyerType('')
			setFundingType('')
		}

		const loadFormDefinition = async () => {
			setIsLoadingDefinition(true)
			setDefinitionError('')
			resetLegacyState()

			try {
				if (shareToken === '') {
					setDefinitionError('Lien invalide.')
					setIsLoadingDefinition(false)
					return
				}

				const response = await form.client.getFormDefinition({
					shareToken,
				})

				if (cancelled) return

				const definition = generateFormDefinition(response)
				const prefill = definition.prefill as JsonMap
				const prefillPersons = getObjectArray(prefill.persons)
				const prefillCorporations = getObjectArray(prefill.corporations)
				const buyerCount = Math.max(prefillPersons.length, prefillCorporations.length, 1)

				setProgramName(definition.formName || DEFAULT_PROGRAM_NAME)
				setSubtitle(definition.city || DEFAULT_SUBTITLE)
				setSection1Values(definition.section1)
				setFormStatus(definition.status)
				setFormSubmittedAt(definition.submittedAt)
				setLoadedDynamicValues(definition.dynamicValues)
				setDynamicFields(definition.dynamicFields)

				const resolvedBuyerType =
					toStringValue(prefill.buyerType) ||
					definition.dynamicValues.buyerType ||
					(prefillCorporations.length > 0 && prefillPersons.length === 0 ? 'corporation' : 'person')
				const resolvedFundingType =
					toStringValue(prefill.fundingType) || definition.dynamicValues.fundingType || ''

				setBuyerType(resolvedBuyerType)
				setFundingType(resolvedFundingType)

				for (let index = 0; index < buyerCount; index += 1) {
					const personEntry = prefillPersons[index] ?? null
					const corporationEntry = prefillCorporations[index] ?? null
					const buyerID =
						toStringValue(personEntry?.id) || toStringValue(corporationEntry?.id) || `buyer-${index}`

					dispatch(addPerson(buyerID))
					dispatch(addCorporation(buyerID))

					if (personEntry) {
						dispatch(setGender({ id: buyerID, value: toStringValue(personEntry.gender) }))
						dispatch(setLastName({ id: buyerID, value: toStringValue(personEntry.lastName) }))
						dispatch(setFirstName({ id: buyerID, value: toStringValue(personEntry.firstName) }))
						dispatch(setPhone({ id: buyerID, value: toStringValue(personEntry.phone) }))
						dispatch(setEmail({ id: buyerID, value: toStringValue(personEntry.email) }))
						dispatch(setBirthDate({ id: buyerID, value: toStringValue(personEntry.birthDate) }))
						dispatch(setBirthCity({ id: buyerID, value: toStringValue(personEntry.birthCity) }))
						dispatch(
							setBirthCountry({ id: buyerID, value: toStringValue(personEntry.birthCountry) }),
						)
						dispatch(setNationality({ id: buyerID, value: toStringValue(personEntry.nationality) }))
						dispatch(setProfession({ id: buyerID, value: toStringValue(personEntry.profession) }))
						dispatch(setAddress({ id: buyerID, value: toStringValue(personEntry.address) }))
						dispatch(setMarried({ id: buyerID, value: toStringValue(personEntry.married) }))
						dispatch(setChildren({ id: buyerID, value: toStringValue(personEntry.children) }))
						dispatch(
							setMarriageCity({ id: buyerID, value: toStringValue(personEntry.marriageCity) }),
						)
						dispatch(
							setMarriageDate({ id: buyerID, value: toStringValue(personEntry.marriageDate) }),
						)
						dispatch(
							setHasContract({ id: buyerID, value: toStringValue(personEntry.hasContract) }),
						)
						dispatch(
							setContractDate({ id: buyerID, value: toStringValue(personEntry.contractDate) }),
						)
						dispatch(
							setContractPlace({ id: buyerID, value: toStringValue(personEntry.contractPlace) }),
						)
						dispatch(
							setContractType({ id: buyerID, value: toStringValue(personEntry.contractType) }),
						)
						dispatch(setIdFile({ id: buyerID, value: toStringValue(personEntry.idFile) }))
						dispatch(
							setNonFinancingDeclarationFile({
								id: buyerID,
								value: toStringValue(personEntry.nonFinancingDeclarationFile),
							}),
						)
					}

					if (corporationEntry) {
						dispatch(
							setCorporationName({ id: buyerID, value: toStringValue(corporationEntry.name) }),
						)
						dispatch(
							setCorporationCapital({
								id: buyerID,
								value: toStringValue(corporationEntry.capital),
							}),
						)
						dispatch(
							setCorporationAddress({
								id: buyerID,
								value: toStringValue(corporationEntry.address),
							}),
						)
						dispatch(
							setCorporationSiren({ id: buyerID, value: toStringValue(corporationEntry.siren) }),
						)
						dispatch(
							setCorporationRegistar({
								id: buyerID,
								value: toStringValue(corporationEntry.registar),
							}),
						)
						dispatch(
							setCorporationDelegateGender({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateGender),
							}),
						)
						dispatch(
							setCorporationDelegateLastName({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateLastName),
							}),
						)
						dispatch(
							setCorporationDelegateFirstName({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateFirstName),
							}),
						)
						dispatch(
							setCorporationDelegatePhone({
								id: buyerID,
								value: toStringValue(corporationEntry.delegatePhone),
							}),
						)
						dispatch(
							setCorporationDelegateEmail({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateEmail),
							}),
						)
						dispatch(
							setCorporationDelegateAddress({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateAddress),
							}),
						)
						dispatch(
							setCorporationKbisFile({
								id: buyerID,
								value: toStringValue(corporationEntry.kbisFile),
							}),
						)
						dispatch(
							setCorporationStatutsFile({
								id: buyerID,
								value: toStringValue(corporationEntry.statutsFile),
							}),
						)
						dispatch(
							setCorporationDelegateIdFile({
								id: buyerID,
								value: toStringValue(corporationEntry.delegateIdFile),
							}),
						)
						dispatch(
							setCorporationNonFinancingDeclarationFile({
								id: buyerID,
								value: toStringValue(corporationEntry.nonFinancingDeclarationFile),
							}),
						)
					}

					if (!personEntry && !corporationEntry && index === 0) {
						dispatch(setLastName({ id: buyerID, value: definition.dynamicValues.primaryLastName || definition.buyerLastName }))
						dispatch(setFirstName({ id: buyerID, value: definition.dynamicValues.primaryFirstName || definition.buyerFirstName }))
						dispatch(setPhone({ id: buyerID, value: definition.dynamicValues.primaryPhone || definition.buyerPhone }))
						dispatch(setEmail({ id: buyerID, value: definition.dynamicValues.primaryEmail || definition.buyerEmail }))
						dispatch(setBirthDate({ id: buyerID, value: definition.dynamicValues.birthDate ?? '' }))
						dispatch(setBirthCity({ id: buyerID, value: definition.dynamicValues.birthCity ?? '' }))
						dispatch(
							setBirthCountry({ id: buyerID, value: definition.dynamicValues.birthCountry ?? '' }),
						)
						dispatch(
							setNationality({ id: buyerID, value: definition.dynamicValues.nationality ?? '' }),
						)
						dispatch(
							setProfession({ id: buyerID, value: definition.dynamicValues.profession ?? '' }),
						)
						dispatch(setAddress({ id: buyerID, value: definition.dynamicValues.address ?? '' }))
						dispatch(setGender({ id: buyerID, value: definition.dynamicValues.gender ?? '' }))
						dispatch(
							setMarried({ id: buyerID, value: definition.dynamicValues.maritalStatus ?? '' }),
						)
						dispatch(
							setChildren({ id: buyerID, value: definition.dynamicValues.childrenCount ?? '' }),
						)
						dispatch(
							setMarriageCity({ id: buyerID, value: definition.dynamicValues.marriageCity ?? '' }),
						)
						dispatch(
							setMarriageDate({ id: buyerID, value: definition.dynamicValues.marriageDate ?? '' }),
						)
						dispatch(
							setHasContract({ id: buyerID, value: definition.dynamicValues.hasContract ?? '' }),
						)
						dispatch(
							setContractDate({ id: buyerID, value: definition.dynamicValues.contractDate ?? '' }),
						)
						dispatch(
							setContractPlace({ id: buyerID, value: definition.dynamicValues.contractPlace ?? '' }),
						)
						dispatch(
							setContractType({ id: buyerID, value: definition.dynamicValues.contractType ?? '' }),
						)
						dispatch(
							setIdFile({ id: buyerID, value: definition.dynamicValues.idDocumentPath ?? '' }),
						)
						dispatch(
							setNonFinancingDeclarationFile({
								id: buyerID,
								value: definition.dynamicValues.proofFundsPath ?? '',
							}),
						)
						dispatch(
							setCorporationDelegateLastName({
								id: buyerID,
								value: definition.dynamicValues.primaryLastName || definition.buyerLastName,
							}),
						)
						dispatch(
							setCorporationDelegateGender({
								id: buyerID,
								value: definition.dynamicValues.delegateGender ?? '',
							}),
						)
						dispatch(
							setCorporationDelegateFirstName({
								id: buyerID,
								value: definition.dynamicValues.primaryFirstName || definition.buyerFirstName,
							}),
						)
						dispatch(
							setCorporationDelegatePhone({
								id: buyerID,
								value: definition.dynamicValues.primaryPhone || definition.buyerPhone,
							}),
						)
						dispatch(
							setCorporationDelegateEmail({
								id: buyerID,
								value: definition.dynamicValues.primaryEmail || definition.buyerEmail,
							}),
						)
						dispatch(
							setCorporationDelegateAddress({
								id: buyerID,
								value: definition.dynamicValues.address ?? '',
							}),
						)
						dispatch(
							setCorporationName({
								id: buyerID,
								value: definition.dynamicValues.corporationName ?? '',
							}),
						)
						dispatch(
							setCorporationCapital({
								id: buyerID,
								value: definition.dynamicValues.corporationCapital ?? '',
							}),
						)
						dispatch(
							setCorporationAddress({
								id: buyerID,
								value: definition.dynamicValues.corporationAddress ?? '',
							}),
						)
						dispatch(
							setCorporationSiren({
								id: buyerID,
								value: definition.dynamicValues.corporationSiren ?? '',
							}),
						)
						dispatch(
							setCorporationRegistar({
								id: buyerID,
								value: definition.dynamicValues.corporationRegistar ?? '',
							}),
						)
						dispatch(
							setCorporationDelegateIdFile({
								id: buyerID,
								value: definition.dynamicValues.idDocumentPath ?? '',
							}),
						)
						dispatch(
							setCorporationKbisFile({
								id: buyerID,
								value: definition.dynamicValues.kbisFile ?? '',
							}),
						)
						dispatch(
							setCorporationStatutsFile({
								id: buyerID,
								value: definition.dynamicValues.statutsFile ?? '',
							}),
						)
						dispatch(
							setCorporationNonFinancingDeclarationFile({
								id: buyerID,
								value: definition.dynamicValues.proofFundsPath ?? '',
							}),
						)
					}
				}

				const fundingPrefill = getObjectValue(prefill.funding)
				dispatch(
					setContributionAmount(
						toStringValue(fundingPrefill?.contributionAmount) ||
							definition.dynamicValues.contributionAmount ||
							'',
					),
				)
				dispatch(
					setLoanAmount(
						toStringValue(fundingPrefill?.loanAmount) || definition.dynamicValues.loanAmount || '',
					),
				)
				dispatch(
					setLoanDuration(
						toStringValue(fundingPrefill?.loanDuration) ||
							definition.dynamicValues.loanDuration ||
							'',
					),
				)
				dispatch(
					setInterestRate(
						toStringValue(fundingPrefill?.interestRate) ||
							definition.dynamicValues.interestRate ||
							'',
					),
				)

				const notaryPrefill = getObjectValue(prefill.notary)
				dispatch(
					setHasNotary(
						toHasNotaryRadioValue(notaryPrefill?.hasNotary ?? definition.dynamicValues.hasNotary),
					),
				)
				dispatch(
					setName(
						toStringValue(notaryPrefill?.name) || definition.dynamicValues.notaryName || '',
					),
				)
				dispatch(
					setCity(
						toStringValue(notaryPrefill?.city) || definition.dynamicValues.notaryCity || '',
					),
				)

				dispatch(
					setTermsAndConditions(
						isTruthy(prefill.termsAndConditions) || isTruthy(definition.dynamicValues.termsAndConditions),
					),
				)
			} catch (error) {
				if (cancelled) return
				console.error('Error loading form definition:', error)
				setDefinitionError('Impossible de charger la definition du formulaire.')
				setProgramName(DEFAULT_PROGRAM_NAME)
				setSubtitle(DEFAULT_SUBTITLE)
				setSection1Values(EMPTY_SECTION1_VALUES)
				setLoadedDynamicValues({})
				setDynamicFields([])
				resetLegacyState()
			} finally {
				if (!cancelled) {
					setIsLoadingDefinition(false)
				}
			}
		}

		loadFormDefinition()

		return () => {
			cancelled = true
		}
	}, [dispatch, form.client, shareToken])

	const handleCloseValidatedModal = () => {
		setReadonly(true)
		setOpenModal(false)
	}

	const modalContent = (
		<div className='modalBody'>
			<h1>Votre formulaire a bien été envoyé</h1>
			<img src={modalArrow} alt='arrow' />
			<p>Vous recevrez un email dans quelques instants.</p>
			<div className='modalActions'>
				<Button onClick={handleCloseValidatedModal}>Fermer</Button>
			</div>
		</div>
	)

	// sendForm is the one request behind all three paths. `completed` is the only
	// thing that separates a draft save from a submission: it flips the status
	// and makes the back-end mail the buyers their dossier.
	const sendForm = (completed: boolean) => {
		const dynamicPayload = buildDynamicPayload({
			loadedDynamicValues,
			buyerType,
			fundingType,
			persons,
			corporations,
			funding,
			notary,
			termsAndConditions: termsAndConditions.termsAndConditions,
		})

		return form.client.send({
			buyerType,
			persons,
			corporations,
			funding,
			notary,
			termsAndConditions: termsAndConditions.termsAndConditions,
			shareToken,
			section1Json: JSON.stringify(section1Values),
			dynamicJson: JSON.stringify(dynamicPayload),
			completed,
			// Empty for the buyer. The broker's session is what lets the back-end
			// take their changes to the property summary, and an incomplete
			// "Valider en l'état".
			token: adminToken,
		})
	}

	const markCompleted = () => {
		setFormStatus(FORM_STATUS_COMPLETED)
		setFormSubmittedAt(new Date().toISOString())
	}

	const handleSubmit = async () => {
		const validated = validator.validate()
		if (!validated) {
			setOpenModal(false)
			setHasValidationError(true)
			return
		}
		setHasValidationError(false)
		setSubmitErrorMessage('')
		setSavedMessage('')

		setSubmitting(true)
		try {
			await sendForm(true)
			markCompleted()
			setOpenModal(true)
		} catch (error) {
			console.error('Error submitting form:', error)
			setSubmitErrorMessage(submitError(error))
		} finally {
			setSubmitting(false)
		}
	}

	// The buyer can stop half-way and come back through the same link: their
	// answers are kept as they are, nothing is checked and nobody is mailed.
	const handleBuyerSave = async () => {
		setSubmitErrorMessage('')
		setSavedMessage('')
		setSubmitting(true)
		try {
			await sendForm(false)
			setSavedMessage(
				'Vos réponses ont été enregistrées. Vous pourrez reprendre le formulaire plus tard avec le même lien.',
			)
		} catch (error) {
			console.error('Error saving form:', error)
			setSubmitErrorMessage(
				"Vos réponses n'ont pas pu être enregistrées. Veuillez réessayer dans quelques instants.",
			)
		} finally {
			setSubmitting(false)
		}
	}

	// A save is never a submission, whether the form is complete or saved in its
	// current state: only an explicit validation completes it.
	const saveForm = async () => {
		setSubmitting(true)
		try {
			await sendForm(false)
			navigate('/form-admin')
		} catch (error) {
			console.error('Error saving form:', error)
		} finally {
			setSubmitting(false)
		}
	}

	// Validating completes the form on the broker's behalf, which stamps it as
	// submitted and has the back-end mail the buyers their dossier.
	const validateForm = async () => {
		setSubmitting(true)
		try {
			await sendForm(true)
			markCompleted()
			setOpenValidatedModal(true)
		} catch (error) {
			console.error('Error validating form:', error)
		} finally {
			setSubmitting(false)
		}
	}

	// Brokers prefill forms on a buyer's behalf, so an incomplete form still has
	// to be savable. Rather than block the save, warn about what is missing and
	// let the admin choose.
	const handleAdminSave = async () => {
		if (!validator.validate()) {
			setHasValidationError(true)
			setPendingIncomplete('save')
			return
		}
		setHasValidationError(false)
		await saveForm()
	}

	const handleAdminValidate = async () => {
		if (!validator.validate()) {
			setHasValidationError(true)
			setPendingIncomplete('validate')
			return
		}
		setHasValidationError(false)
		await validateForm()
	}

	// validate() paints the per-field errors as a side effect, so they are
	// already on screen behind the modal: closing it is enough to show them.
	const handleBackToForm = () => setPendingIncomplete(null)

	const handleContinueIncomplete = async () => {
		const action = pendingIncomplete
		setPendingIncomplete(null)
		if (action === 'validate') await validateForm()
		else await saveForm()
	}

	const incompleteModalContent = (
		<div className='modalBody'>
			<h1>Certains champs obligatoires ne sont pas remplis</h1>
			<p>
				{pendingIncomplete === 'validate'
					? "Vous pouvez revenir au formulaire pour voir les champs concernés, ou le valider en l'état. Les acquéreurs recevront alors le récapitulatif par email."
					: "Vous pouvez revenir au formulaire pour voir les champs concernés, ou enregistrer le formulaire en l'état."}
			</p>
			<div className='modalActions'>
				<Button onClick={handleBackToForm}>Revenir au formulaire</Button>
				<Button onClick={handleContinueIncomplete}>
					{pendingIncomplete === 'validate' ? "Valider en l'état" : "Enregistrer en l'état"}
				</Button>
			</div>
		</div>
	)

	const validatedModalContent = (
		<div className='modalBody'>
			<h1>Le formulaire a bien été validé</h1>
			<img src={modalArrow} alt='' />
			<p>Les acquéreurs vont recevoir le récapitulatif par email dans quelques instants.</p>
			<div className='modalActions'>
				<Button onClick={() => navigate('/form-admin')}>Retour à mes formulaires</Button>
			</div>
		</div>
	)

	const fillWithPreset = (type: 'person-loan' | 'person-cash' | 'corporation-loan' | 'corporation-cash') => {
		dispatch(resetPersons())
		dispatch(resetCorporations())
		dispatch(resetFunding())
		dispatch(resetNotary())
		dispatch(setTermsAndConditions(false))

		const id = nanoid()
		dispatch(addPerson(id))
		dispatch(addCorporation(id))

		const isCorp = type.startsWith('corporation')
		const isLoan = type.endsWith('loan')

		setBuyerType(isCorp ? 'corporation' : 'person')
		setFundingType(isLoan ? 'loan' : 'cash')

		if (!isCorp) {
			dispatch(setGender({ id, value: 'mr' }))
			dispatch(setFirstName({ id, value: 'Jean' }))
			dispatch(setLastName({ id, value: 'Dupont' }))
			dispatch(setPhone({ id, value: '+33612345678' }))
			dispatch(setEmail({ id, value: 'jean.dupont@example.com' }))
			dispatch(setBirthDate({ id, value: '1985-06-15' }))
			dispatch(setBirthCity({ id, value: 'Lyon' }))
			dispatch(setBirthCountry({ id, value: 'France' }))
			dispatch(setNationality({ id, value: 'Française' }))
			dispatch(setProfession({ id, value: 'Ingénieur' }))
			dispatch(setAddress({ id, value: '12 rue de la Paix, 75001 Paris' }))
			dispatch(setMarried({ id, value: 'married' }))
			dispatch(setChildren({ id, value: '2' }))
			dispatch(setMarriageCity({ id, value: 'Paris' }))
			dispatch(setMarriageDate({ id, value: '2010-09-20' }))
			dispatch(setHasContract({ id, value: `${id}-personHasContract-no` }))
		} else {
			dispatch(setCorporationName({ id, value: 'Dupont SAS' }))
			dispatch(setCorporationCapital({ id, value: '100000' }))
			dispatch(setCorporationAddress({ id, value: '15 avenue des Champs, 75008 Paris' }))
			dispatch(setCorporationSiren({ id, value: '123456789' }))
			dispatch(setCorporationRegistar({ id, value: 'Paris' }))
			dispatch(setCorporationDelegateGender({ id, value: 'mr' }))
			dispatch(setCorporationDelegateFirstName({ id, value: 'Jean' }))
			dispatch(setCorporationDelegateLastName({ id, value: 'Dupont' }))
			dispatch(setCorporationDelegatePhone({ id, value: '+33612345678' }))
			dispatch(setCorporationDelegateEmail({ id, value: 'jean.dupont@dupont-sas.com' }))
			dispatch(setCorporationDelegateAddress({ id, value: '15 avenue des Champs, 75008 Paris' }))
		}

		if (isLoan) {
			dispatch(setContributionAmount('50000'))
			dispatch(setLoanAmount('400000'))
			dispatch(setLoanDuration('20'))
			dispatch(setInterestRate('3.8'))
		} else {
			dispatch(setContributionAmount('450000'))
		}

		dispatch(setHasNotary('hasNotary-yes'))
		dispatch(setName('Jean Martin'))
		dispatch(setCity('Paris'))
		dispatch(setTermsAndConditions(true))
	}

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.code === 'Enter' || event.code === 'NumpadEnter') {
			event.preventDefault()
			if (submitting) return
			if (isAdmin) handleAdminSave()
			else handleSubmit()
		}
	}

	// The forms are listed, and searched, on the admin page: a search started
	// here is carried over to it.
	const handleSearchSubmit = (value: string) => {
		const query = value.trim()
		if (query === '') return
		navigate(`/form-admin?q=${encodeURIComponent(query)}`)
	}

	const formClass = readonly ? 'readonly' : ''
	const addButtonText =
		buyerType === 'corporation' ? 'Ajouter un représentant' : 'Ajouter un co-acquéreur'
	const canRenderForm = !isLoadingDefinition && !isVerifyingToken && definitionError === ''
	const buyerTypeOptions = dynamicFields.find(field => field.key === 'buyerType')?.options ?? []
	const fundingTypeOptions = dynamicFields.find(field => field.key === 'fundingType')?.options ?? []
	// The answers the whole form shares, for the schema's conditions. Each
	// section adds its own buyer's answers to them.
	const formAnswers: Record<string, string> = { buyerType, fundingType, hasNotary: notary.hasNotary }
	const extraDynamicFields = dynamicFields.filter(
		field =>
			!MAPPED_DYNAMIC_KEYS.has(field.key) &&
			rules.shows(field.key, { ...loadedDynamicValues, ...formAnswers }),
	)

	return (
		<>
			<Layout
				showNav={isAdmin}
				showSearchBar={isAdmin}
				searchValue={searchQuery}
				onSearchChange={setSearchQuery}
				onSearchSubmit={handleSearchSubmit}
				headerContent={
					<>
						<h1 id='program'>{programName}</h1>
						<h2 id='city'>{subtitle}</h2>
						{canRenderForm && (
							<div className={`formStatus ${formStatus === FORM_STATUS_COMPLETED ? 'formStatus--completed' : 'formStatus--pending'}`}>
								{formStatus === FORM_STATUS_COMPLETED
									? `Complété le ${formatSubmittedAt(formSubmittedAt)}`
									: 'Non complété'}
							</div>
						)}
					</>
				}
			>
			<form
				id='form'
				className={formClass}
				onKeyDown={handleKeyDown}
				onSubmit={event => event.preventDefault()}
				noValidate
			>
			<p id='description'>Formulaire de renseignement pour votre acquisition immobilière</p>
			{isLoadingDefinition && <p className='text-center'>Chargement du formulaire...</p>}
			{definitionError !== '' && <p className='text-center error'>{definitionError}</p>}
			{canRenderForm && (
				<>
					<Section1
						values={section1Values}
						readOnly={!isAdmin}
						onChange={(key, value) => setSection1Values(current => ({ ...current, [key]: value }))}
					/>
					{Object.values(persons).map((person, index) => (
						<Section2
							key={person.id}
							id={person.id}
							position={index}
							onRemoveBuyer={handleRemoveBuyer}
							buyerType={buyerType}
							onSetBuyerType={handleSetBuyerType}
							rules={rules}
							answers={formAnswers}
							buyerTypeOptions={buyerTypeOptions}
						/>
					))}
					{rules.asks('buyerType') && (
						<ButtonIconText
							icon={ajouter}
							text={addButtonText}
							onClick={event => {
								event.preventDefault()
								handleAddBuyer()
							}}
						/>
					)}
					<Funding
						fundingType={fundingType}
						onSetFundingType={handleSetFundingType}
						rules={rules}
						answers={formAnswers}
						fundingTypeOptions={fundingTypeOptions}
					/>
					<Notary rules={rules} answers={formAnswers} />
					<Files
						buyerType={buyerType}
						fundingType={fundingType}
						rules={rules}
						answers={formAnswers}
					/>
					{rules.asks('termsAndConditions') && (
						<Terms required={rules.requires('termsAndConditions')} />
					)}
					{extraDynamicFields.length > 0 && (
						<DynamicFields
							fields={extraDynamicFields}
							values={loadedDynamicValues}
							onChange={(key, value) => {
								setLoadedDynamicValues(current => ({ ...current, [key]: value }))
							}}
						/>
					)}
					{hasValidationError && (
						<span className='error' style={{ marginBottom: '12px' }}>Veuillez vérifier votre saisie, certains champs sont incorrects ou manquants.</span>
					)}
					{submitErrorMessage !== '' && (
						<span className='error' role='alert' style={{ marginBottom: '12px' }}>
							{submitErrorMessage}
						</span>
					)}
					{savedMessage !== '' && (
						<span className='formNotice' role='status'>
							{savedMessage}
						</span>
					)}
					{isAdmin ? (
						<div className='formActions'>
							<ButtonIconText
								id='save'
								invertIcon={true}
								text='Sauvegarder'
								icon={arrow}
								loading={submitting}
								onClick={handleAdminSave}
							/>
							<ButtonIconText
								id='send'
								invertIcon={true}
								text='Valider'
								icon={arrow}
								loading={submitting}
								onClick={handleAdminValidate}
							/>
						</div>
					) : (
						<div className='formActions'>
							<ButtonIconText
								id='save'
								invertIcon={true}
								text='Sauvegarder'
								icon={arrow}
								loading={submitting}
								onClick={handleBuyerSave}
							/>
							<ButtonIconText
								id='send'
								invertIcon={true}
								text='Envoyer'
								icon={arrow}
								loading={submitting}
								onClick={handleSubmit}
							/>
						</div>
					)}
				</>
			)}
				{openModal && <Modal>{modalContent}</Modal>}
				{openValidatedModal && <Modal>{validatedModalContent}</Modal>}
				{pendingIncomplete !== null && <Modal>{incompleteModalContent}</Modal>}
			</form>
			</Layout>
			{process.env.NODE_ENV === 'development' && (
				<div style={{
					position: 'fixed', bottom: '16px', right: '16px',
					background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
					padding: '8px 12px', zIndex: 9999, display: 'flex',
					flexDirection: 'column', gap: '4px', fontSize: '12px', boxShadow: '0 2px 8px rgba(0,0,0,.15)',
				}}>
					<strong>Dev — presets</strong>
					{([
						{ label: 'Personne physique + Prêt', type: 'person-loan' },
						{ label: 'Personne physique + Comptant', type: 'person-cash' },
						{ label: 'Personne morale + Prêt', type: 'corporation-loan' },
						{ label: 'Personne morale + Comptant', type: 'corporation-cash' },
					] as const).map(preset => (
						<button
							key={preset.type}
							type='button'
							disabled={!canRenderForm}
							onClick={() => fillWithPreset(preset.type)}
							style={{ cursor: 'pointer', padding: '4px 8px', textAlign: 'left' }}
						>
							{preset.label}
						</button>
					))}
				</div>
			)}
		</>
	)
}

export default Form
